import React, { useEffect, useState, useMemo } from "react";
import { Table, Typography, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getProfitLoss } from "../../../../redux/budgetSlice/budgetSlice";
import { useLocation } from "react-router-dom";
import { generateMonthExecution } from '../../../../pages/BudgetSubmission/constants';
import { generateColumns, parseDMY } from '../../../../utils/utilFunctions';
import { addMonths, differenceInCalendarMonths, differenceInMonths, format, parse } from 'date-fns';
import { getProjectTimeline } from '../../../../utils/getProjectTimeline';

const { Text } = Typography;

const parseNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : 0;
};

const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

const getDynamicUpto = (row = {}, fyStart) => {
        let sum = parseNum(row.upto);

        Object.keys(row).forEach((key) => {
                const match = /^(\d{4})-\d{4}_formatted$/.exec(key);
                if (!match) return;

                const fyStartYear = Number(match[1]);
                if (fyStartYear < fyStart) {
                        sum += parseNum(row[key]);
                }
        });

        return sum.toFixed(2);
};

const ProjectLossTable = ({ version = "C" }) => {
        const { projectDates, profitLossData, profitLossVersion } = useSelector((state) => state.budget);
        const { systemDate } = useSelector((state) => state.otherProjects);

        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        const totalSum = 'Total';
        const location = useLocation();
        const dispatch = useDispatch();
        const ioNo = location.state?.ioNo || '--';
        const revisedNo = location.state?.rvNo || '--';
        const formattedUptoDate = projectDates?.UPTO_DATE ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy") : '';
        const zeroDateParsed = parseDMY(projectDates?.ZERO_DATE);
        const uptoDateParsed = parseDMY(projectDates?.UPTO_DATE);
        const expectedDateParsed = parseDMY(projectDates?.EXPECTED_DATE);
        const unitPlanDateParsed = parseDMY(projectDates?.UNIT_PLAN_DATE);
        let startMonth = 4;
        let startYear = CURRENT_FY_START;

        // const endYear = expectedDateParsed ? expectedDateParsed.getFullYear() : 2026;
        // const endMonth = expectedDateParsed ? expectedDateParsed.getMonth() + 1 : 12;

        let maxDataEndYear = 0;

        const sourceData =
                version === "C"
                        ? profitLossData
                        : profitLossVersion;

        if (Array.isArray(sourceData) && sourceData.length > 0) {
                const firstRow = sourceData[0];

                const fyKeys = Object.keys(firstRow).filter(
                        (key) => /^\d{4}-\d{4}$/.test(key)
                );

                if (fyKeys.length > 0) {
                        maxDataEndYear = Math.max(
                                ...fyKeys.map((fy) => Number(fy.split("-")[1]))
                        );
                }
        }

        // const expectedEndYear = expectedDateParsed
        //         ? expectedDateParsed.getFullYear()
        //         : 0;

        // const finalEndYear = Math.max(maxDataEndYear, expectedEndYear);
        // const endYear = finalEndYear ? finalEndYear - 1 : startYear + 1;

        // const unitPlanEndYear = unitPlanDateParsed
        //         ? unitPlanDateParsed.getFullYear()
        //         : 0;

        // const finalEndYear = unitPlanEndYear;
        // const endYear = finalEndYear || startYear;

        // const endMonth = unitPlanDateParsed
        //         ? unitPlanDateParsed.getMonth() + 1
        //         : 12;

        const startMonthNumber = zeroDateParsed && uptoDateParsed ? differenceInCalendarMonths(addMonths(uptoDateParsed, 1), zeroDateParsed) + 1 : 0;

        const { endYear, endMonth, totalMonth, higherEndDate } = getProjectTimeline({
                zeroDate: zeroDateParsed,
                expectedDate: expectedDateParsed,
                unitPlanDate: unitPlanDateParsed,
        });

        // const totalMonth = zeroDateParsed && unitPlanDateParsed
        //         ? differenceInMonths(unitPlanDateParsed, zeroDateParsed) + 1
        //         : 0;

        const [data, setData] = useState([]);
        const role = sessionStorage.getItem('role');

        let columns = [
                {
                        title: 'Description',
                        dataIndex: 'description',
                        key: 'description',
                        width: 300,
                        align: 'center',
                        render: (text, record, index) => {
                                if (record.addPackage) {
                                        return {
                                                children: (
                                                        <div className="w-full">
                                                                <Text>{text}</Text> &nbsp;
                                                                <Button
                                                                        type="link"
                                                                        icon={<PlusOutlined />}
                                                                        onClick={() => handleAddPackage(index)}>
                                                                        Add Package
                                                                </Button>
                                                        </div>
                                                ),
                                                props: { colSpan: 4 },
                                        };
                                }

                                if (record.boldHeading) {
                                        return (
                                                <div className="w-[200px]">
                                                        <Text strong>{text}</Text> &nbsp;
                                                </div>
                                        );
                                }

                                return <div className="w-[200px]">{text}</div>;
                        },
                },
                {
                        title: `CCI Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'upto',
                        key: 'upto',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        const handleAddPackage = (index) => {
                const newData = [...data];
                newData.splice(index + 1, 0, {
                        key: `new-${Date.now()}`,
                        type: 'ME',
                        code: 'ME04',
                        description: 'New Package',
                });
                setData(newData);
        };

        useEffect(() => {
                if (role !== 'ADMIN' && role !== 'SUPER_ADMIN') {
                        dispatch(getProfitLoss({ IO_NO: ioNo, REV_NO: revisedNo }));
                }
        }, []);

        useEffect(() => {
                const sourceData =
                        version === "C"
                                ? profitLossData
                                : profitLossVersion;

                if (Array.isArray(sourceData)) {
                        const sourceDataWithDynamicUpto = sourceData.map((row) => ({
                                ...row,
                                upto: getDynamicUpto(row, CURRENT_FY_START),
                        }));

                        setData([
                                {
                                        key: "1",
                                        description: "Month of Execution",
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth
                                        ),
                                        boldHeading: true,
                                },
                                ...sourceDataWithDynamicUpto,
                        ]);
                }
        }, [profitLossData, profitLossVersion, version, CURRENT_FY_START]);


        return (
                <Table
                        columns={generateColumns(
                                startYear,
                                endYear,
                                startMonth,
                                endMonth,
                                columns,
                                higherEndDate || projectDates?.EXPECTED_DATE || projectDates?.UNIT_PLAN_DATE,
                                'Total',
                        )}
                        dataSource={data}
                        pagination={false}
                        bordered
                        scroll={{ x: 'max-content' }}
                        className={` custom-ant-table`}
                        rowClassName={(record, index) => {
                                if (index === 0) {
                                        return 'table-row-dark';
                                } else {
                                        return 'table-row-light';
                                }
                        }}
                />
        );
};

export default ProjectLossTable;